import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.6 — Step 5 "Staff roster" for the prospect-facing intake portal.
 *
 * Fields mirror `intakeStep5Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   staff: IntakeStaffEntry[]  (1..200; unique emails; ≥ 1 owner)
 *     - firstName  (1..60)
 *     - lastName   (1..60)
 *     - email      (rfc-shape)
 *     - phone?     (US regex, optional)
 *     - role       (owner | manager | receptionist | walker | groomer)
 *     - sendInviteOnSubmit (default true)
 *     - notes?     (≤ 280 chars, optional)
 *   csvImportSource?: string  (optional free-text audit tag)
 *
 * The editor holds each row locally (allowing partial rows while the
 * prospect is typing) and serializes on every onChange so the draft
 * payload tracks the UI. Required-per-row fields (firstName, lastName,
 * email, role) are validated server-side at submit time — the client
 * surfaces the server issues by path (e.g. `staff.0.email`). Client-side
 * format nudges (email shape, US phone shape) fire inline before submit
 * to save the prospect a round-trip.
 *
 * On first mount with an empty staff list, we seed one starter row
 * pre-populated from the invite email with role=owner so the schema's
 * "at least one owner" refine is trivially satisfied. The prospect is
 * free to replace it.
 */

export const INTAKE_ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'walker', label: 'Walker' },
  { value: 'groomer', label: 'Groomer' },
] as const;
type IntakeRoleSlug = (typeof INTAKE_ROLE_OPTIONS)[number]['value'];

const VALID_ROLES: ReadonlySet<IntakeRoleSlug> = new Set(
  INTAKE_ROLE_OPTIONS.map((o) => o.value),
);

const MAX_STAFF_ROWS = 200;
const MAX_NOTES_LENGTH = 280;

type StaffRow = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: IntakeRoleSlug | '';
  sendInviteOnSubmit: boolean;
  notes: string;
};

type Step5Draft = {
  staff: StaffRow[];
  csvImportSource: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function boolField(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readRole(value: unknown): IntakeRoleSlug | '' {
  if (typeof value !== 'string') return '';
  return VALID_ROLES.has(value as IntakeRoleSlug)
    ? (value as IntakeRoleSlug)
    : '';
}

function readStaffRow(value: unknown): StaffRow {
  const row = isRecord(value) ? value : {};
  return {
    firstName: stringField(row.firstName),
    lastName: stringField(row.lastName),
    email: stringField(row.email),
    phone: stringField(row.phone),
    role: readRole(row.role),
    sendInviteOnSubmit: boolField(row.sendInviteOnSubmit, true),
    notes: stringField(row.notes),
  };
}

function readStaff(value: unknown): StaffRow[] {
  if (!Array.isArray(value)) return [];
  return value.map(readStaffRow);
}

function readStep5(draft: Record<string, unknown>): Step5Draft {
  return {
    staff: readStaff(draft.staff),
    csvImportSource: stringField(draft.csvImportSource),
  };
}

/**
 * Serialize a row into the schema's object shape. Optional fields are
 * omitted (not emitted as empty strings) so Zod's `.optional()` holds
 * and refine-driven error paths like `staff.0.phone` don't fire when
 * the prospect simply hasn't filled phone yet.
 */
function serializeStaffRow(row: StaffRow): Record<string, unknown> {
  const out: Record<string, unknown> = {
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    sendInviteOnSubmit: row.sendInviteOnSubmit,
  };
  if (row.role !== '') out.role = row.role;
  if (row.phone.trim() !== '') out.phone = row.phone.trim();
  if (row.notes.trim() !== '') out.notes = row.notes.trim();
  return out;
}

function serializeStep5(next: Step5Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {
    staff: next.staff.map(serializeStaffRow),
  };
  if (next.csvImportSource.trim() !== '') {
    out.csvImportSource = next.csvImportSource.trim();
  }
  return out;
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step5-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

const PHONE_REGEX = /^\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

function describeEmailError(value: string): string | null {
  if (value.trim() === '') return null;
  return /.+@.+\..+/.test(value) ? null : 'Enter a valid email.';
}

function describePhoneError(value: string): string | null {
  if (value.trim() === '') return null;
  return PHONE_REGEX.test(value) ? null : 'Enter a US phone number.';
}

/**
 * Case-insensitive duplicate detection. Returns a set of row indices that
 * share an email with another row. Blank emails are ignored — they're
 * an incomplete row, not a duplicate.
 */
function detectDuplicateEmailIndices(rows: ReadonlyArray<StaffRow>): Set<number> {
  const byEmail = new Map<string, number[]>();
  rows.forEach((row, idx) => {
    const email = row.email.trim().toLowerCase();
    if (email === '') return;
    const list = byEmail.get(email) ?? [];
    list.push(idx);
    byEmail.set(email, list);
  });
  const dupes = new Set<number>();
  for (const list of byEmail.values()) {
    if (list.length > 1) list.forEach((idx) => dupes.add(idx));
  }
  return dupes;
}

function hasOwner(rows: ReadonlyArray<StaffRow>): boolean {
  return rows.some((r) => r.role === 'owner');
}

function makeStarterRow(inviteEmail: string): StaffRow {
  return {
    firstName: '',
    lastName: '',
    email: inviteEmail ?? '',
    phone: '',
    role: 'owner',
    sendInviteOnSubmit: true,
    notes: '',
  };
}

function makeBlankRow(): StaffRow {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    sendInviteOnSubmit: true,
    notes: '',
  };
}

export type Step5StaffRosterProps = IntakeShellRenderProps;

export function Step5StaffRoster({
  stepDraft,
  onChange,
  intake,
  issues,
}: Step5StaffRosterProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const value = useMemo(() => readStep5(stepDraft), [stepDraft]);
  const { staff, csvImportSource } = value;

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  // Seed a starter owner row on first mount when the list is empty.
  // Pre-fills the invite email so the prospect sees it and can confirm
  // or replace. Matches the Step 2 / Step 4 mount-default pattern.
  useEffect(() => {
    if (!Array.isArray(stepDraft.staff) || stepDraft.staff.length === 0) {
      const starter = makeStarterRow(intake.inviteEmail);
      onChange(
        serializeStep5({
          staff: [starter],
          csvImportSource,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitRows = (rows: StaffRow[]): void => {
    onChange(serializeStep5({ staff: rows, csvImportSource }));
  };

  const patchRow = (idx: number, patch: Partial<StaffRow>): void => {
    const next = staff.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    commitRows(next);
  };

  const addRow = (): void => {
    if (staff.length >= MAX_STAFF_ROWS) return;
    commitRows([...staff, makeBlankRow()]);
  };

  const removeRow = (idx: number): void => {
    if (staff.length <= 1) return;
    commitRows(staff.filter((_, i) => i !== idx));
  };

  const onCsvSourceChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(
      serializeStep5({ staff, csvImportSource: e.target.value }),
    );
  };

  const duplicateIndices = useMemo(
    () => detectDuplicateEmailIndices(staff),
    [staff],
  );
  const ownerMissing = staff.length > 0 && !hasOwner(staff);

  const rootServerError = serverErrorFor('staff');

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 5 — Staff roster"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        List the people who will use the admin app on day one. At least one
        owner is required. We'll send magic-link invites to the folks you
        mark for invite when your tenant is provisioned.
      </p>

      {ownerMissing ? (
        <p
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step5-owner-missing"
        >
          At least one staff member must have the Owner role.
        </p>
      ) : null}

      {rootServerError ? (
        <p
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step5-root-servererror"
        >
          {rootServerError}
        </p>
      ) : null}

      <ul className="ferro-intake__staff" data-testid="intake-step5-staff-list">
        {staff.map((row, idx) => {
          const firstNameServerError = serverErrorFor(
            `staff.${idx}.firstName`,
          );
          const lastNameServerError = serverErrorFor(`staff.${idx}.lastName`);
          const emailServerError = serverErrorFor(`staff.${idx}.email`);
          const phoneServerError = serverErrorFor(`staff.${idx}.phone`);
          const roleServerError = serverErrorFor(`staff.${idx}.role`);
          const notesServerError = serverErrorFor(`staff.${idx}.notes`);
          const emailClientError = describeEmailError(row.email);
          const phoneClientError = describePhoneError(row.phone);
          const isDuplicate = duplicateIndices.has(idx);

          return (
            <li
              key={idx}
              className="ferro-intake__staffRow"
              data-testid={`intake-step5-staff-row-${idx}`}
            >
              <div className="ferro-intake__staffRow-grid">
                <label className="ferro-field">
                  <span className="ferro-field__label">First name</span>
                  <input
                    type="text"
                    className="ferro-field__input"
                    value={row.firstName}
                    maxLength={60}
                    aria-invalid={firstNameServerError ? 'true' : undefined}
                    aria-describedby={
                      firstNameServerError
                        ? fieldErrorId(`row-${idx}-firstName`)
                        : undefined
                    }
                    onChange={(e) =>
                      patchRow(idx, { firstName: e.target.value })
                    }
                    data-testid={`intake-step5-row-${idx}-firstName`}
                  />
                  {firstNameServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-firstName`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {firstNameServerError}
                    </span>
                  ) : null}
                </label>

                <label className="ferro-field">
                  <span className="ferro-field__label">Last name</span>
                  <input
                    type="text"
                    className="ferro-field__input"
                    value={row.lastName}
                    maxLength={60}
                    aria-invalid={lastNameServerError ? 'true' : undefined}
                    aria-describedby={
                      lastNameServerError
                        ? fieldErrorId(`row-${idx}-lastName`)
                        : undefined
                    }
                    onChange={(e) =>
                      patchRow(idx, { lastName: e.target.value })
                    }
                    data-testid={`intake-step5-row-${idx}-lastName`}
                  />
                  {lastNameServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-lastName`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {lastNameServerError}
                    </span>
                  ) : null}
                </label>

                <label className="ferro-field">
                  <span className="ferro-field__label">Email</span>
                  <input
                    type="email"
                    className="ferro-field__input"
                    value={row.email}
                    aria-invalid={
                      emailServerError || emailClientError || isDuplicate
                        ? 'true'
                        : undefined
                    }
                    aria-describedby={
                      emailServerError || emailClientError || isDuplicate
                        ? fieldErrorId(`row-${idx}-email`)
                        : undefined
                    }
                    onChange={(e) => patchRow(idx, { email: e.target.value })}
                    data-testid={`intake-step5-row-${idx}-email`}
                  />
                  {emailServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-email`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {emailServerError}
                    </span>
                  ) : isDuplicate ? (
                    <span
                      id={fieldErrorId(`row-${idx}-email`)}
                      role="alert"
                      className="ferro-form__error"
                      data-testid={`intake-step5-row-${idx}-email-duplicate`}
                    >
                      This email is already on another row.
                    </span>
                  ) : emailClientError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-email`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {emailClientError}
                    </span>
                  ) : null}
                </label>

                <label className="ferro-field">
                  <span className="ferro-field__label">Phone (optional)</span>
                  <input
                    type="tel"
                    className="ferro-field__input"
                    value={row.phone}
                    aria-invalid={
                      phoneServerError || phoneClientError ? 'true' : undefined
                    }
                    aria-describedby={
                      phoneServerError || phoneClientError
                        ? fieldErrorId(`row-${idx}-phone`)
                        : undefined
                    }
                    onChange={(e) => patchRow(idx, { phone: e.target.value })}
                    data-testid={`intake-step5-row-${idx}-phone`}
                  />
                  {phoneServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-phone`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {phoneServerError}
                    </span>
                  ) : phoneClientError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-phone`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {phoneClientError}
                    </span>
                  ) : null}
                </label>

                <label className="ferro-field">
                  <span className="ferro-field__label">Role</span>
                  <select
                    className="ferro-field__input"
                    value={row.role}
                    aria-invalid={roleServerError ? 'true' : undefined}
                    aria-describedby={
                      roleServerError
                        ? fieldErrorId(`row-${idx}-role`)
                        : undefined
                    }
                    onChange={(e) =>
                      patchRow(idx, {
                        role: readRole(e.target.value),
                      })
                    }
                    data-testid={`intake-step5-row-${idx}-role`}
                  >
                    <option value="">Select a role…</option>
                    {INTAKE_ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {roleServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-role`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {roleServerError}
                    </span>
                  ) : null}
                </label>

                <label className="ferro-field ferro-intake__checkbox">
                  <input
                    type="checkbox"
                    checked={row.sendInviteOnSubmit}
                    onChange={(e) =>
                      patchRow(idx, {
                        sendInviteOnSubmit: e.target.checked,
                      })
                    }
                    data-testid={`intake-step5-row-${idx}-send-invite`}
                  />
                  <span>Send magic-link invite on provisioning</span>
                </label>

                <label className="ferro-field ferro-intake__staffRow-notes">
                  <span className="ferro-field__label">Notes (optional)</span>
                  <textarea
                    className="ferro-field__input"
                    value={row.notes}
                    maxLength={MAX_NOTES_LENGTH}
                    aria-invalid={notesServerError ? 'true' : undefined}
                    aria-describedby={
                      notesServerError
                        ? fieldErrorId(`row-${idx}-notes`)
                        : undefined
                    }
                    onChange={(e) => patchRow(idx, { notes: e.target.value })}
                    data-testid={`intake-step5-row-${idx}-notes`}
                  />
                  {notesServerError ? (
                    <span
                      id={fieldErrorId(`row-${idx}-notes`)}
                      role="alert"
                      className="ferro-form__error"
                    >
                      {notesServerError}
                    </span>
                  ) : null}
                </label>
              </div>

              <div className="ferro-intake__staffRow-actions">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => removeRow(idx)}
                  disabled={staff.length <= 1}
                  aria-label={`Remove staff member ${idx + 1}`}
                  data-testid={`intake-step5-row-${idx}-remove`}
                >
                  Remove
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="ferro-btn ferro-btn--secondary"
        onClick={addRow}
        disabled={staff.length >= MAX_STAFF_ROWS}
        data-testid="intake-step5-add-row"
      >
        Add staff member
      </button>

      <label className="ferro-field">
        <span className="ferro-field__label">
          CSV import reference (optional)
        </span>
        <input
          type="text"
          className="ferro-field__input"
          value={csvImportSource}
          placeholder="e.g. gingr-export-2026-04.csv"
          onChange={onCsvSourceChange}
          data-testid="intake-step5-csv-source"
        />
      </label>
    </form>
  );
}
